import {
  Button,
  Helper,
  Icon,
  IconButton,
  Image,
  TextEditor,
  Tooltip,
  Typography
} from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { UploadLogoModal } from '@hero/components/modals';
import { constants } from '@hero/configs';
import { MAX_SENDER_NAME_LENGTH } from '@hero/constants';
import { useUpdateBranding } from '@hero/services/organization-service/hooks';
import { useNotification } from '@hero/store/hooks';

import { ImagePreview } from '../ImagePreview';
import { MessageLibraryModal } from '../MessageLibraryModal';

import * as S from './style';

export function RecipientExperiencePreview({
  errors = {
    senderName: false,
    message: false
  },
  giftWrapperUrl,
  isContactLess,
  linkPreview,
  logoUrl,
  message,
  onBranding,
  onMessageParams,
  senderName,
  warnings
}) {
  const { t } = useTranslation();
  const { showError } = useNotification();

  const theme = useTheme();
  const nameInputRef = useRef(null);
  const { mutateAsync: updateBranding } = useUpdateBranding();

  const [activeDevice, setActiveDevice] = useState('desktop');
  const [showMessageLibrary, setShowMessageLibrary] = useState(false);

  const [isEditingName, setIsEditingName] = useState(false);

  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const [localMessage, setLocalMessage] = useState(message);
  const [localSenderName, setLocalSenderName] = useState(senderName);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  useEffect(() => {
    setLocalSenderName(senderName);
    setLocalMessage(message);
  }, [message, senderName]);

  function handleCloseEditingSenderName() {
    setIsEditingName(false);
  }

  function handleCloseEditingMessage() {
    setLocalMessage(message);
    setIsEditingMessage(false);
  }

  const hasSenderNameWarning = warnings?.find(w => w.key.includes('getSenderNameRule'));
  const hasMessageWarning = warnings?.find(w => w.key.includes('getMessageRule'));

  return (
    <S.RecipientExperiencePreviewWrapper>
      <S.RecipientExperiencePreviewHeader>
        <Typography variant="body1Bold">{t('recipientExperiencePreviewDesc')}</Typography>
        <S.PreviewHeaderWrapper>
          <Button
            onClick={() => {
              window.open(linkPreview, '_blank');
            }}
            target="_blank"
            variant="link"
            size="sm"
            color="fg"
            startIcon={<Icon name="preview" />}
          >
            {t('livePreview')}
          </Button>
          <S.DeviceWrapper>
            <S.DeviceButton
              isActive={activeDevice === 'desktop'}
              variant="link"
              size="md"
              color="fg"
              iconName="desktop"
              onClick={() => setActiveDevice('desktop')}
            />
            <S.DeviceButton
              isActive={activeDevice === 'mobile'}
              variant="link"
              size="md"
              color="fg"
              iconName="mobile"
              onClick={() => setActiveDevice('mobile')}
            />
          </S.DeviceWrapper>
        </S.PreviewHeaderWrapper>
      </S.RecipientExperiencePreviewHeader>
      <S.RecipientExperiencePreviewBodyFrame>
        <S.RecipientExperiencePreviewBody
          isMobile={activeDevice === 'mobile'}
          wrapperUrl={giftWrapperUrl}
        >
          <S.PreviewItemIconButton
            iconName="edit"
            variant="contained"
            color="secondary"
            top={activeDevice === 'mobile' ? 8 : 20}
            right={activeDevice === 'mobile' ? 8 : 20}
            onClick={() => {
              window?.pageLayoutContent?.scrollTo({
                top: window?.pageLayoutContent?.scrollTop + 400,
                behavior: 'smooth'
              });
            }}
          />
          <S.RecipientExperiencePreviewBodyContent isMobile={activeDevice === 'mobile'}>
            <Image
              src={activeDevice === 'mobile' ? theme.images.smartgiftFav : theme.images.logoFull}
              height={32}
              width={activeDevice === 'mobile' ? 32 : 105}
              className="logo"
              style={{
                minWidth: 24
              }}
            />

            <S.PreviewItem>
              {logoUrl ? (
                <Tooltip
                  content={
                    <Typography
                      variant="body2"
                      color="bg"
                      style={{ textAlign: 'center', width: 162 }}
                    >
                      {t('removeAndUploadLogo')}
                    </Typography>
                  }
                  size="md"
                >
                  <ImagePreview
                    src={logoUrl}
                    active={true}
                    showActions={false}
                    hasRadio={false}
                    style={{ maxWidth: 100 }}
                  />
                  <S.PreviewItemIconButton
                    onClick={() => {
                      onBranding({ logoUrl: 'null' });
                    }}
                    iconName="delete"
                    color="fg"
                    top={-12}
                    right={-12}
                  />
                </Tooltip>
              ) : (
                <Tooltip
                  content={
                    <Typography
                      variant="body2"
                      color="bg"
                      style={{ textAlign: 'center', width: 162 }}
                    >
                      {t('uploadYourLogo')}
                    </Typography>
                  }
                >
                  <S.UploadImageButtonWrapper>
                    <S.UploadButton>
                      <Icon name="upload" size="lg" />
                      <Typography variant="body2SemiBold">{t('uploadLogoOptional')}</Typography>
                    </S.UploadButton>
                  </S.UploadImageButtonWrapper>

                  <S.PreviewItemIconButton
                    onClick={() => setIsUploadModalOpen(true)}
                    iconName="upload"
                    color="fg"
                    top={-12}
                    right={-12}
                  />
                </Tooltip>
              )}
            </S.PreviewItem>
            <Typography variant="body1">{t('youReceivedGiftFrom')}:</Typography>

            <S.PreviewItem>
              <Tooltip active={!isEditingName} content={t('editSenderName')}>
                <S.SenderNameInput
                  isMobile={activeDevice === 'mobile'}
                  readOnly={!isEditingName}
                  value={
                    isEditingName ? localSenderName : localSenderName || t('yourNameOrCompanyName')
                  }
                  onBlur={handleCloseEditingSenderName}
                  onChange={e => {
                    setLocalSenderName(e.target.value);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === 'Tab') {
                      setIsEditingName(false);
                      onMessageParams({ senderName: e.target.value });
                    }

                    if (e.key === 'Escape') {
                      setIsEditingName(false);
                    }
                  }}
                  ref={nameInputRef}
                  maxLength={MAX_SENDER_NAME_LENGTH}
                  error={errors.senderName || hasSenderNameWarning}
                  isEditing={isEditingName}
                  onClick={() => {
                    setIsEditingName(true);
                    setTimeout(() => {
                      nameInputRef.current.focus();
                    }, 100);
                  }}
                  helperProps={{ ...(isEditingName && { showCounter: true }) }}
                />
              </Tooltip>
              {!isEditingName && (
                <S.PreviewItemIconButton
                  onClick={() => {
                    setIsEditingName(true);
                    setTimeout(() => {
                      nameInputRef.current.focus();
                    }, 100);
                  }}
                  right={-10}
                  top={-10}
                  iconName={errors.senderName || hasSenderNameWarning ? 'error' : 'edit'}
                  color="fg"
                  hasError={errors.senderName || hasSenderNameWarning}
                />
              )}
              {isEditingName && (
                <S.ActionButtons top={35} right={5}>
                  <IconButton variant="text" color="error" onClick={handleCloseEditingSenderName}>
                    <Icon
                      color="var(--color-components-button-secondary-text-fg)"
                      name="close"
                      size="lg"
                    />
                  </IconButton>

                  <IconButton
                    variant="text"
                    color="success"
                    onMouseDown={e => {
                      e.stopPropagation();

                      setIsEditingName(false);
                      onMessageParams({ senderName: localSenderName });
                    }}
                  >
                    <Icon
                      color="var(--color-components-button-secondary-text-fg)"
                      name="success"
                      size="lg"
                    />
                  </IconButton>
                </S.ActionButtons>
              )}
            </S.PreviewItem>

            <S.PreviewItem>
              <S.GiftMessageWrapper
                hasError={errors.message || hasMessageWarning}
                isMobile={activeDevice === 'mobile'}
              >
                {!isEditingMessage ? (
                  <Tooltip content={t('editGiftMessage')}>
                    <div
                      className="preview-message"
                      dangerouslySetInnerHTML={{
                        __html: localMessage || t('writeYourGiftMessage')
                      }}
                      onClick={() => setIsEditingMessage(true)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') setIsEditingMessage(true);
                      }}
                      tabIndex="0"
                    />
                  </Tooltip>
                ) : (
                  <S.MessageEditorWrapper isMobile={activeDevice === 'mobile'}>
                    <TextEditor
                      value={localMessage}
                      onKeyDown={e => {
                        if (e.key === 'Tab') {
                          setIsEditingMessage(false);
                          onMessageParams({ message: localMessage });
                        }

                        if (e.key === 'Escape') {
                          handleCloseEditingMessage();
                        }
                      }}
                      onChange={e => {
                        setLocalMessage(e);
                      }}
                      limit={constants?.general?.giftMessageLimit}
                      showToolbar
                      {...(!isContactLess && {
                        toolbarPrefixData: [{ label: t('name'), name: 'name' }]
                      })}
                    >
                      {({ charCount }) => (
                        <S.MessageEditorFooter>
                          <Helper
                            text={t('lengthCharacters', {
                              length: charCount,
                              limit: constants?.general?.giftMessageLimit
                            })}
                          />
                          <Button
                            onClick={() => setShowMessageLibrary(true)}
                            variant="link"
                            size="sm"
                            color="fg"
                            startIcon={<Icon name="library" />}
                          >
                            {t('library')}
                          </Button>
                        </S.MessageEditorFooter>
                      )}
                    </TextEditor>
                  </S.MessageEditorWrapper>
                )}
              </S.GiftMessageWrapper>

              {!isEditingMessage && (
                <S.PreviewItemIconButton
                  onClick={() => setIsEditingMessage(true)}
                  right={-10}
                  top={-10}
                  iconName={errors.message || hasMessageWarning ? 'error' : 'edit'}
                  color="fg"
                  hasError={errors.message || hasMessageWarning}
                  error={errors.message || hasMessageWarning}
                />
              )}
              {isEditingMessage && (
                <S.ActionButtons bottom={10} right={8}>
                  <IconButton variant="text" color="error" onClick={handleCloseEditingMessage}>
                    <Icon
                      color="var(--color-components-button-secondary-text-fg)"
                      name="close"
                      size="lg"
                    />
                  </IconButton>

                  <IconButton
                    disabled={!localMessage}
                    variant="text"
                    color="success"
                    onMouseDown={e => {
                      e.stopPropagation();

                      setIsEditingMessage(false);
                      onMessageParams({ message: localMessage });
                    }}
                  >
                    <Icon
                      color="var(--color-components-button-secondary-text-fg)"
                      name="success"
                      size="lg"
                    />
                  </IconButton>
                </S.ActionButtons>
              )}
            </S.PreviewItem>
          </S.RecipientExperiencePreviewBodyContent>
        </S.RecipientExperiencePreviewBody>
      </S.RecipientExperiencePreviewBodyFrame>

      <UploadLogoModal
        isOpen={isUploadModalOpen}
        setIsOpen={setIsUploadModalOpen}
        uploadAction={({ cdnUrl }) => {
          updateBranding({
            branding: {
              logoUrl: cdnUrl
            }
          });
          onBranding({ logoUrl: cdnUrl });
        }}
      />

      <MessageLibraryModal
        isOpen={showMessageLibrary}
        onClose={() => setShowMessageLibrary(false)}
        onClick={({ message }) => {
          if (message.length > constants?.general?.giftMessageLimit) {
            showError({
              type: 'toast',
              message: t('characterLimitWarning', {
                limit: constants?.general?.giftMessageLimit
              })
            });
            return;
          }
          setLocalMessage(message);
          setIsEditingMessage(false);
          onMessageParams({ message });
        }}
      />
    </S.RecipientExperiencePreviewWrapper>
  );
}

RecipientExperiencePreview.propTypes = {
  errors: PropTypes.shape({
    senderName: PropTypes.bool,
    message: PropTypes.bool
  }),
  giftWrapperUrl: PropTypes.string,
  linkPreview: PropTypes.string,
  logoUrl: PropTypes.string,
  message: PropTypes.string,
  onBranding: PropTypes.func.isRequired,
  onMessageParams: PropTypes.func.isRequired,
  senderName: PropTypes.string
};
